import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SparklesIcon, CheckCircleIcon, CheckBadgeIcon, MapPinIcon, ShieldCheckIcon, UserGroupIcon, ArrowRightIcon, CalendarDaysIcon, ClipboardDocumentCheckIcon, UserIcon, StarIcon, Bars3Icon, XMarkIcon, HeartIcon } from "@heroicons/react/24/outline";
import logo from "../Signup/img/logo.svg";
import cicoaIcon from "./img/cicoa.png";
import mhmIcon from "./img/mhm.png";
import unitedIcon from "./img/united.png";
import massIcon from "./img/mass.png";
import '../Signup/landing.scss';
import PublicHeader from "components/Header/PublicHeader";
import caregiverImage from "./img/landingComment.jpg";

export default function LandingTherapy() {
    const [currentTestimonial, setCurrentTestimonial] = useState(0);

    function QuoteIcon({ className }) {
        return (
            <svg className={className} fill="currentColor" viewBox="0 0 24 24">
                <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
            </svg>
        );
    }

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const htmlElement = document.documentElement;
        htmlElement.classList.add("tailwind-html");

        return () => {
            htmlElement.classList.remove("tailwind-html");
        };
    }, []);

    useEffect(() => {
        document.body.classList.add("tailwind-body");

        return () => {
            document.body.classList.remove("tailwind-body");
        };
    }, []);

    const benefits = [
        {
            title: "Comfort of Home",
            description: "Skip the waiting room and receive professional care from your favorite chair",
            detail: "We encourage caregivers, spouses, or pets to stay close by if that helps you feel grounded.",
            icon: ShieldCheckIcon,
        },
        {
            title: "Compassionate Experts",
            description: "Our licensed therapists specialize in helping you navigate anxiety, grief, and life transitions",
            detail: "Every visit starts with a friendly catch-up to see how you're feeling before we dive in.",
            icon: HeartIcon,
        },
        {
            title: "Medicare Coverage",
            description: "Peace of mind for your wallet – services are covered by most Medicare plans",
            detail: "We coordinate with your doctors and handle the paperwork so you can focus on feeling better.",
            icon: UserGroupIcon,
        },
        {
            title: "Simple to Start",
            description: "Getting started is easy with no referrals required – just reach out to check eligibility",
            detail: "You can invite a loved one to the first call so everyone hears the plan together.",
            icon: CheckCircleIcon,
        },
    ];

    const steps = [
        {
            title: "Book Your Visit",
            description: "Schedule your appointment online at your convenience",
            icon: CalendarDaysIcon,
        },
        {
            title: "We Verify Coverage",
            description: "We verify your Medicare coverage and eligibility",
            icon: ClipboardDocumentCheckIcon,
        },
        {
            title: "The Home Visit",
            description: "Your therapist visits your home for the session",
            icon: UserIcon,
        },
        {
            title: "Continuous Care",
            description: "We handle the billing so your care comes first",
            icon: HeartIcon,
        },
    ];

    const testimonials = [
        {
            name: "Sarah W.",
            location: "Boston, MA",
            image: "https://emissaryhealth.org/img/senior2.png",
            text: "Found the perfect caregiver for my mother. The matching process was seamless and the care provided has been exceptional!",
            categories: ["Personal Care", "Mobility Help"],
        },
        {
            name: "Michael T.",
            location: "Manchester, NH",
            image: "https://emissaryhealth.org/img/senior3.jpg",
            text: "The caregivers are incredibly professional and compassionate. They've made such a positive impact on our family's life.",
            categories: ["Companionship", "Memory Care"],
        },
        {
            name: "Emily R.",
            location: "Miami, FL",
            image: "https://emissaryhealth.org/img/senior1.png",
            text: "Exceptional service from start to finish. The care coordinator was helpful and found us the perfect match within days.",
            categories: ["Skilled Nursing", "Medical Care"],
        },
    ];

    const connectionImage = "https://images.unsplash.com/photo-1503437313881-503a91226402?auto=format&fit=crop&w=1200&q=80";
    const journalingImage = "https://images.unsplash.com/photo-1448932223592-d1fc686e76ea?auto=format&fit=crop&w=1200&q=80";

    return (
        <div className="landing-page bg-white w-full">
            <div className="min-h-screen bg-slate-50">
                {/* Header */}
                <PublicHeader />

                {/* Main Content */}
                <main className="relative isolate bg-white">
                    {/* Hero Section */}
                    <section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-26">
                        <div className="grid gap-16 lg:grid-cols-2 items-center">
                            <div>
                                <div className="inline-flex items-center rounded-full px-4 py-1 text-sm font-medium bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-700/10 mb-8 hover:bg-sky-100 transition-colors duration-300 group">
                                    <SparklesIcon className="h-4 w-4 mr-2 text-sky-600 group-hover:animate-pulse" />
                                    Medicare-Covered Care in Greater Boston
                                </div>
                                <h1 className="text-4xl text-sky-500 font-bold tracking-tight sm:text-4xl">
                                    Compassionate In-Home Therapy for Medicare Beneficiaries
                                </h1>
                                <p className="mt-6 text-lg leading-8 text-gray-600">
                                    Life brings changes that can feel heavy. Let our licensed therapists bring support and
                                    connection to your doorstep.
                                </p>
                                <div className="mt-8 space-y-4">
                                    {[
                                        "Dedicated care team that learns your story",
                                        "Friendly onboarding with no referrals required",
                                        "Evidence-based therapy tailored to older adults",
                                    ].map((item) => (
                                        <div key={item} className="flex items-start gap-3">
                                            <CheckCircleIcon className="h-6 w-6 text-sky-500" />
                                            <p className="text-base text-gray-700">{item}</p>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 flex flex-wrap items-center gap-4">
                                    <Link
                                        to="/auth/signup"
                                        className="group relative rounded-md bg-sky-500 px-8 py-4 text-base font-semibold text-white shadow-sm hover:bg-sky-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 transition-all duration-300"
                                    >
                                        <span className="relative z-10 flex items-center">
                                            Check Your Eligibility
                                            <ArrowRightIcon className="ml-2 -mr-1 h-5 w-5 inline-block transition-transform duration-300 group-hover:translate-x-1" />
                                        </span>
                                        <div className="absolute inset-0 rounded-md bg-gradient-to-r from-sky-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    </Link>

                                    <Link
                                        to="/about/how"
                                        className="group relative flex h-[55px] items-center justify-center rounded-md border border-sky-500 bg-transparent px-8 text-base font-semibold text-sky-500 shadow-sm hover:bg-sky-600 hover:text-white hover:border-sky-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600  transition-all duration-300"
                                    >
                                        <span className="relative z-10 flex items-center">
                                            Learn How Visits Work
                                            <ArrowRightIcon className="ml-2 -mr-1 h-5 w-5 inline-block transition-transform duration-300 group-hover:translate-x-1" />
                                        </span>
                                        <div className="absolute inset-0 rounded-md bg-gradient-to-r from-sky-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    </Link>
                                </div>

                                <p className="font-style: italic mt-6 text-sm text-gray-500">
                                    Services are typically covered by
                                    Medicare.
                                </p>

                            </div>
                            <div className="relative">
                                <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-sky-100 to-white blur-3xl opacity-70 -z-10"></div>
                                <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200 bg-white">
                                    <img
                                        src={caregiverImage}
                                        alt="Caregiver sitting with an older adult during a therapy visit"
                                        className="h-[520px] w-full object-cover"
                                        loading="lazy"
                                    />
                                    {/* <div className="p-6">
                                        <p className="text-lg font-semibold text-slate-900">“I feel like someone is truly listening.”</p>
                                        <p className="mt-2 text-sm text-slate-600">
                                            - Gloria, Emissary Health participant
                                        </p>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Benefits Section */}
                    <section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-30">
                        <p className="text-sm font-semibold uppercase tracking-wide text-blue-500">
                            What care feels like
                        </p>
                        <h2 className="text-4xl text-blue-950 font-bold tracking-tight sm:text-4xl">
                            Every visit is designed to feel like a trusted friend stopping by
                        </h2>
                        <p className="mt-4 text-lg text-gray-600">
                            Our team focuses on the emotional cues that matter most – soft voices, unhurried pauses, and time
                            for stories that matter to you.
                        </p>
                        <div className="grid gap-16 lg:grid-cols-[1.2fr_0.8fr] items-center">
                            <div>
                                <dl className="mt-12 grid gap-8 sm:grid-cols-2">
                                    {benefits.map((benefit) => (
                                        <div key={benefit.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm hover:shadow-md transition-all duration-300">
                                            <dt className="flex items-center gap-3 text-lg font-semibold text-slate-900">
                                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50">
                                                    <benefit.icon className="h-6 w-6 text-sky-600" aria-hidden="true" />
                                                </div>
                                                {benefit.title}
                                            </dt>
                                            <dd className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
                                                <p>{benefit.description}</p>
                                                <p className="text-slate-500 font-style: italic">{benefit.detail}</p>
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                            <div className="relative mt-12 lg:mt-0">
                                <div className="relative w-full max-w-[540px] mx-auto h-[200px] sm:h-[260px] lg:h-[280px]">



                                    {testimonials.map((testimonial, index) => (
                                        <div
                                            key={index}
                                            className={`absolute inset-0 transition-all duration-700 ease-in-out ${index === currentTestimonial
                                                ? "opacity-100 translate-y-0 z-20"
                                                : index ===
                                                    (currentTestimonial + 1) % testimonials.length
                                                    ? "opacity-85 translate-y-20 scale-[0.95] blur-[2px] z-10"
                                                    : "opacity-0 translate-y-4 z-0"
                                                }`}
                                        >
                                            <div
                                                className={`w-full h-full rounded-2xl bg-white/90 backdrop-blur-sm px-6 py-5 shadow-lg shadow-slate-200/50 ring-1 ring-slate-100 flex flex-col ${index === currentTestimonial ? "" : "bg-white/80"
                                                    }`}
                                            >
                                                <div className="flex gap-1 mb-2">
                                                    {[...Array(5)].map((_, i) => (
                                                        <StarIcon
                                                            key={i}
                                                            className="h-4 w-4 text-yellow-400"
                                                        />
                                                    ))}
                                                    <span className="ml-1 text-xs font-medium text-slate-500">
                                                        Verified
                                                    </span>
                                                </div>
                                                <div className="relative flex-1">
                                                    <QuoteIcon className="absolute -top-1 -left-1 h-5 w-5 text-slate-200 transform -scale-x-100" />
                                                    <p className="pl-6 text-xs sm:text-sm font-medium leading-snug text-slate-600 text-left">
                                                        {testimonial.text}
                                                    </p>
                                                </div>
                                                <div className="mt-3 flex items-center justify-between gap-x-2 border-t border-slate-100 pt-3">
                                                    <div className="flex items-center gap-x-2">
                                                        <div className="relative flex-shrink-0">
                                                            <img
                                                                src={testimonial.image}
                                                                alt={testimonial.name}
                                                                className="h-10 w-10 rounded-full object-cover ring-2 ring-white"
                                                            />
                                                            <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-sky-50 ring-1 ring-white flex items-center justify-center">
                                                                <CheckBadgeIcon
                                                                    className="h-2.5 w-2.5 text-sky-500"
                                                                    aria-hidden="true"
                                                                />
                                                            </span>
                                                        </div>
                                                        <div className="text-left min-w-0">
                                                            <p className="text-xs font-semibold text-slate-800 truncate">
                                                                {testimonial.name}
                                                            </p>
                                                            <p className="text-xs text-slate-500 flex items-center gap-x-0.5 truncate">
                                                                <MapPinIcon className="h-3 w-3 flex-shrink-0" />
                                                                <span className="truncate">{testimonial.location}</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1 hidden sm:flex flex-shrink-0">
                                                        {testimonial.categories.slice(0, 1).map((category, i) => (
                                                            <span
                                                                key={i}
                                                                className="inline-flex items-center rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-600 ring-1 ring-inset ring-sky-100 whitespace-nowrap"
                                                            >
                                                                {category}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* How It Works Section */}
                    <section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32 bg-gradient-to-b from-white to-sky-50">
                        <div className="mx-auto max-w-3xl text-center">
                            <p className="text-sm font-semibold uppercase tracking-wide text-blue-500">Familiar and simple</p>
                            <h2 className="mt-4 text-3xl font-bold text-blue-950 sm:text-4xl">
                                A traditional care journey – brought right to your living room
                            </h2>
                            <p className="mt-4 text-lg text-gray-600">
                                We combine the comfort of face-to-face care with a simple,
                                hassle-free booking process. No complex hurdles – just a straightforward way to get the
                                support you need.
                            </p>
                        </div>
                        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-12 lg:max-w-none lg:grid-cols-4">
                                {steps.map((step, index) => {
                                    const Icon = step.icon;
                                    return (
                                        <div key={step.title} className="group relative flex flex-col rounded-2xl bg-white p-8 shadow-lg ring-1 ring-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                            <div className="flex items-center gap-x-4">
                                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 group-hover:bg-sky-100 transition-colors duration-300">
                                                    <span className="text-xl font-semibold text-sky-600 group-hover:scale-110 transition-transform duration-300">{index + 1}</span>
                                                </div>
                                                <dt className="text-lg font-semibold leading-7 text-gray-900">
                                                    {step.title}
                                                </dt>
                                            </div>
                                            <div className="mt-4 flex items-center gap-3 text-sm font-medium text-slate-500">
                                                <Icon className="h-5 w-5 text-sky-500" />
                                                <span>Step {index + 1}</span>
                                            </div>
                                            <dd className="mt-4 flex flex-col text-base leading-7 text-gray-600">
                                                <p>{step.description}</p>
                                                <p className="mt-3 text-sm text-slate-500">{step.tone}</p>
                                            </dd>
                                            {index < steps.length - 1 && (
                                                <div className="absolute -right-4 top-1/2 hidden lg:block">
                                                    <ArrowRightIcon className="h-8 w-8 text-sky-300 group-hover:text-sky-400 transition-colors duration-300 group-hover:translate-x-1" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </dl>
                        </div>
                    </section>


                    {/*Partner Orgatinizations Section */}
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 pt-12 bg-gradient-to-b from-sky-50 to-white">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-blue-950 mb-6 text-center">
                            Partner Organizations
                        </h3>
                        <div className="mx-auto grid grid-cols-4 items-center gap-x-8 gap-y-10 sm:max-w-xl sm:grid-cols-2 sm:gap-x-10 lg:mx-0 lg:max-w-none lg:grid-cols-4">
                            <a
                                href="https://cicoa.org"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="col-span-1 flex justify-center transition-opacity opacity-75 hover:opacity-100"
                            >
                                <img
                                    className="h-12 object-contain opacity-75"
                                    src={cicoaIcon}
                                    alt="CICOA"
                                />
                            </a>
                            <a
                                href="https://mentalhealthmn.org"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="col-span-1 flex justify-center transition-opacity opacity-75 hover:opacity-100"
                            >
                                <img
                                    className="h-12 object-contain opacity-75"
                                    src={mhmIcon}
                                    alt="Mental Health Minnesota"
                                />
                            </a>
                            <a
                                href="https://www.uhc.com/lp/find-affordable-health-plans"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="col-span-1 flex justify-center transition-opacity opacity-75 hover:opacity-100"
                            >
                                <img
                                    className="h-12 object-contain opacity-75"
                                    src={unitedIcon}
                                    alt="United Healthcare"
                                />
                            </a>
                            <a
                                href="https://www.mass.gov/topics/executive-office-of-health-and-human-services"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="col-span-1 flex justify-center transition-opacity opacity-75 hover:opacity-100"
                            >
                                <img
                                    className="h-12 object-contain opacity-75"
                                    src={massIcon}
                                    alt="Massachusetts department of health and human services"
                                />
                            </a>
                        </div>
                    </div>

                    {/* CTA Section */}
                    <section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32">
                        <div className="relative overflow-hidden rounded-3xl border border-slate-100 bg-white px-6 py-16 text-center shadow-xl sm:px-16">
                            <div className="absolute inset-0 pointer-events-none">
                                <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-sky-50/80 to-transparent"></div>
                                <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-rose-50/80 to-transparent"></div>
                            </div>
                            <div className="relative">
                                <p className="text-sm font-semibold uppercase tracking-wide text-blue-500">We’re here to listen</p>
                                <h2 className="mx-auto mt-4 max-w-3xl text-3xl font-bold tracking-tight  text-sky-950 sm:text-4xl">
                                    Let’s plan a visit that feels gentle, familiar, and fully supported by Medicare.
                                </h2>
                                <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-slate-600">
                                    Getting started is as simple as picking a time on our calendar. Once
                                    you book, our team handles the eligibility checks behind the scenes to ensure you are
                                    covered before your session begins.
                                </p>
                                <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                                    <Link
                                        to="/auth/signup"
                                        className="inline-flex items-center justify-center rounded-full bg-sky-500 px-8 py-4 text-base font-semibold text-white shadow-lg transition hover:bg-sky-600"
                                    >
                                        Book Your First Visit
                                        <ArrowRightIcon className="ml-2 h-5 w-5" />
                                    </Link>
                                </div>
                                <p className="mx-auto mt-8 max-w-xl text-sm leading-6 text-slate-500">
                                    This outreach form does not guarantee coverage or establish a patient relationship. We’ll confirm benefits together.
                                </p>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
}